<template>
  <section class="shopping-cart-resume">
    <div class="shopping-cart-resume__prices-wrapper">
      <div class="shopping-cart-resume__type">
        <Pill cart class="shopping-cart-resume__pill">
          {{ shoppingCart.length }} Item{{ shoppingCart.length > 1 ? 's' : '' }}
        </Pill>
        Total today <span>{{ currencySymbol }}{{ total }}</span
        ><span class="shopping-cart-resume__label shopping-cart-resume__taxes">
          {{ taxes > 0 || isFreeTrial ? '' : 'before tax' }}
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Pill from '@/components/shared/Pill.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'ShoppingCartResume',
  components: {
    Pill,
  },
  data() {
    return {
      checkoutPricing: '',
      discount: '',
      subtotal: '',
      taxes: '',
      total: '',
      currencySymbol: '$',
      isPromoAvailable: false,
    }
  },
  computed: {
    ...mapState({
      customCoupon: state => state.shoppingCart.coupon,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      paymentEntries: state => state.subscriptions.paymentEntries,
      availablePlans: state => state.products.availablePlans,
      availableBootcamps: state => state.products.availableBootcamps,
      availableCertifications: state => state.products.availableCertifications,
      selectedProductType: state => state.products.selectedProductType,
      planCodes: state => state.products.planCodes,
      promoField: state => state.subscriptions.promotionalField,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
    }),
    coupon: {
      set(value) {
        this.$store.dispatch('shoppingCart/updateCoupon', value)
      },
      get() {
        return this.customCoupon
      },
    },
    selectedPlanCode() {
      return this.selectedPlan?.merchant_id ?? ''
    },
    selectedPlanName() {
      const name = this.selectedPlan?.name ?? ''
      return name.replace('INE', '')
    },
    isPromo() {
      return this.promoField === CONSTANTS.PLANS.PROMO
    },
    isFreeTrial() {
      return this.selectedPlan?.plan_trial
    },
    isPremiumPlanSelected() {
      return this.selectedPlanCode === CONSTANTS.PLANS.PREMIUM
    },
    subscriptionsInCart() {
      const codes = this.availablePlans.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    certificationsInCart() {
      const codes = this.availableCertifications.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    bootcampsInCart() {
      const codes = this.availableBootcamps.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
  },
  watch: {
    coupon(value) {
      if (!value && !value.length) {
        this.setupPricing()
      }
    },
    shoppingCart(items) {
      this.setupPricing()
    },
    paymentEntries: {
      deep: true,
      handler(paymentEntries) {
        const { country, zip } = paymentEntries
        this.checkoutPricing
          .address({
            country,
            postal_code: zip,
          })
          .catch(err => this.handleError(err))
          .done()
      },
    },
  },
  methods: {
    ...mapActions({
      addProductToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeProductFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
    }),
    removeSelectedProduct(product) {
      const exists = this.shoppingCart.find(i => i.id === product.id)
      if (exists) {
        this.removeProductFromShoppingCart(exists)
      } else {
        this.addProductToShoppingCart(product)
      }
      if (this.shoppingCart.length > 0) {
        this.setupPricing()
      }
    },
    handlePricing(prices) {
      const {
        currency: { symbol },
        now: { discount, subscriptions: subtotal, taxes, total },
      } = prices
      if (symbol && symbol.length) {
        this.currencySymbol = symbol
      }
      if (this.isFreeTrial) {
        this.discount = '0.00'
        this.subtotal = '0.00'
        this.taxes = '0.00'
        this.total = '0.00'
        const freeTrialTotal = '0.00'
        this.$store.dispatch('subscriptions/updateCheckoutTotal', freeTrialTotal)
      }
      if (!this.isFreeTrial) {
        this.discount = discount
        this.subtotal = subtotal
        this.taxes = taxes
        this.total = total
        this.$store.dispatch('subscriptions/updateCheckoutTotal', total)
      }
    },
    handleError(error) {
      console.error(error)
    },
    createSubscription() {
      const { country, zip: postal_code } = this.paymentEntries
      const subscription = window.recurly.Pricing.Subscription().plan(this.selectedPlanCode)
      return this.checkoutPricing
        .subscription(subscription.catch(error => this.handleError(error)).done())
        .address({ country, postal_code })
        .coupon(this.customCoupon)
    },
    addSingleProduct(item) {
      return this.checkoutPricing.adjustment({
        id: '1',
        itemCode: item.merchant_id,
        quantity: 1,
        taxExempt: false,
      })
    },
    calculatePricing() {
      if (this.shoppingCart && this.shoppingCart.length) {
        this.shoppingCart.map(item => {
          if (item.merchant_id && this.planCodes.includes(item.merchant_id)) {
            this.createSubscription()
              .catch(error => this.handleError(error))
              .done()
          } else {
            this.addSingleProduct(item)
              .catch(error => this.handleError(error))
              .done()
          }
        })
      }
    },
    setupPricing() {
      this.checkoutPricing = ''
      this.checkoutPricing = window.recurly.Pricing.Checkout()
      this.checkoutPricing.on('change', price => this.handlePricing(price))
      this.calculatePricing()
    },
  },
  created() {
    this.isPromoAvailable = this.coupon.length > 0
    this.setupPricing()
    setTimeout(() => {
      this.checkoutPricing
        .coupon(this.customCoupon)
        .catch(error => this.handleError(error))
        .done()
    }, 500)
  },
}
</script>

<style lang="scss">
.shopping-cart-resume {
  font-family: var(--font-medium);
  color: var(--white);
  background: var(--mid-gray-translucid-gradient-three), var(--dark-brighter);
  border-radius: 20px;
  padding: 20px;
  &__type {
    display: flex;
    align-items: baseline;
  }
  &__taxes {
    color: var(--light-blue-gray);
    font-size: rem(14px);
    font-style: italic;
    margin-left: 3px;
  }
  &__pill {
    margin-right: 10px;
  }
}
</style>
